import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  LinearProgress,
  Card,
  CardContent,
  Fade,
  Grow,
  Skeleton,
  Avatar,
} from "@mui/material";
import { motion } from "framer-motion";
import { styled, keyframes } from "@mui/material/styles";

// Custom animations
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const logoFloat = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, #f8fafc 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  borderRadius: theme.shape.borderRadius * 2,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-2px)",
  },
}));

const AnimatedContainer = styled(Box)(({ theme }) => ({
  animation: `${pulse} 2s ease-in-out infinite`,
}));

const ShimmerBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.grey[200]} 0%, ${theme.palette.grey[100]} 50%, ${theme.palette.grey[200]} 100%)`,
  backgroundSize: "200px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
  borderRadius: theme.shape.borderRadius,
}));

const NavyCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: "#1e3a8a",
  "& .MuiCircularProgress-svg": {
    filter: "drop-shadow(0 2px 4px rgba(30, 58, 138, 0.3))",
  },
}));

const NavyLinearProgress = styled(LinearProgress)(({ theme }) => ({
  backgroundColor: "#e5e7eb",
  borderRadius: 6,
  height: 8,
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#1e3a8a",
    borderRadius: 6,
    background: "linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  animation: `${logoFloat} 3s ease-in-out infinite`,
  marginBottom: theme.spacing(2),
}));

const RegistrationLoadingContainer = styled(Box)(({ theme }) => ({
  minHeight: "50vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%)",
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: "linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)",
  },
}));

/**
 * A reusable loading state component with various display styles
 *
 * @param {Object} props - Component props
 * @param {string} props.type - Loading indicator type: "linear", "circular", "fullscreen", "contained", "skeleton", "card", "registration"
 * @param {string} props.message - Optional message to display during loading
 * @param {string} props.color - MUI color for the loader (primary, secondary, etc.)
 * @param {Object} props.sx - Additional styles to apply to container
 * @param {boolean} props.showProgress - Whether to show progress percentage
 * @param {number} props.progress - Progress value (0-100)
 * @param {string} props.eventId - Event ID for logo (required for registration type)
 * @param {string} props.eventName - Event name to display
 * @param {string} props.logoUrl - Custom logo URL
 */
const LoadingState = ({
  type = "linear",
  message = "Loading...",
  color = "primary",
  sx = {},
  showProgress = false,
  progress = 0,
  eventId,
  eventName,
  logoUrl,
}) => {
  const renderLoadingDots = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            delay: i * 0.2,
          }}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "#1e3a8a",
          }}
        />
      ))}
    </Box>
  );

  // Get logo URL for registration loading
  const getLogoUrl = () => {
    if (logoUrl) return logoUrl;
    if (eventId) {
      // Use XTravex logo for eventId 1745879876
      if (eventId === "1745879876") {
        return "https://api-admin.eventegration.com/public/logo/1745879876.png?t=1766999775406";
      }
      return `https://api-reg.eventegration.com/images/logo/${eventId}.png`;
    }
    return null;
  };

  switch (type) {
    case "registration":
      return (
        <Fade in timeout={500}>
          <RegistrationLoadingContainer sx={sx}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{ textAlign: "center" }}
            >
              {/* Event Logo */}
              {getLogoUrl() && (
                <LogoContainer>
                  <Avatar
                    src={getLogoUrl()}
                    sx={{
                      width: 80,
                      height: 80,
                      margin: "0 auto",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      border: "3px solid #ffffff",
                    }}
                    imgProps={{
                      onError: (e) => {
                        e.target.style.display = "none";
                      },
                    }}
                  />
                </LogoContainer>
              )}

              {/* Event Name */}
              {eventName && (
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    background:
                      "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {eventName}
                </Typography>
              )}

              {/* Loading Spinner */}
              <AnimatedContainer sx={{ mb: 3 }}>
                <NavyCircularProgress
                  size={56}
                  thickness={4}
                  variant={showProgress ? "determinate" : "indeterminate"}
                  value={progress}
                />
              </AnimatedContainer>

              {/* Loading Message */}
              <Typography
                variant="h6"
                sx={{
                  color: "#111827",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                {message}
              </Typography>

              {/* Loading Dots */}
              {renderLoadingDots()}

              {/* Progress Text */}
              {showProgress && (
                <Typography
                  variant="body1"
                  sx={{
                    color: "#1e3a8a",
                    fontWeight: 700,
                    mt: 2,
                  }}
                >
                  {Math.round(progress)}%
                </Typography>
              )}

              {/* Additional Info */}
              <Box sx={{ mt: 3, opacity: 0.7 }}>
                <Typography variant="caption" color="text.secondary">
                  Please wait while we prepare your registration form
                </Typography>
              </Box>
            </motion.div>
          </RegistrationLoadingContainer>
        </Fade>
      );

    case "linear":
      return (
        <Fade in timeout={500}>
          <Box sx={{ width: "100%", ...sx }}>
            <NavyLinearProgress
              variant={showProgress ? "determinate" : "indeterminate"}
              value={progress}
              sx={{ mb: 2 }}
            />
            {message && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    color: "#4b5563",
                    fontWeight: 500,
                  }}
                >
                  {message}
                </Typography>
                {renderLoadingDots()}
                {showProgress && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1e3a8a",
                      fontWeight: 600,
                      ml: 1,
                    }}
                  >
                    {Math.round(progress)}%
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Fade>
      );

    case "circular":
      return (
        <Grow in timeout={500}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              ...sx,
            }}
          >
            <AnimatedContainer>
              <NavyCircularProgress
                size={48}
                thickness={4}
                variant={showProgress ? "determinate" : "indeterminate"}
                value={progress}
              />
            </AnimatedContainer>
            {message && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#111827",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {message}
                </Typography>
                {renderLoadingDots()}
                {showProgress && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#1e3a8a",
                      fontWeight: 700,
                      mt: 1,
                    }}
                  >
                    {Math.round(progress)}%
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Grow>
      );

    case "fullscreen":
      return (
        <Fade in timeout={800}>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%)",
              zIndex: 9999,
              ...sx,
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <StyledCard sx={{ p: 4, textAlign: "center", minWidth: 300 }}>
                <CardContent>
                  <NavyCircularProgress
                    size={64}
                    thickness={4}
                    variant={showProgress ? "determinate" : "indeterminate"}
                    value={progress}
                  />
                  {message && (
                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#111827",
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        {message}
                      </Typography>
                      {renderLoadingDots()}
                      {showProgress && (
                        <Typography
                          variant="h5"
                          sx={{
                            color: "#1e3a8a",
                            fontWeight: 700,
                            mt: 2,
                          }}
                        >
                          {Math.round(progress)}%
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            </motion.div>
          </Box>
        </Fade>
      );

    case "contained":
      return (
        <Grow in timeout={600}>
          <StyledCard
            sx={{
              p: 4,
              minHeight: 150,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              ...sx,
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 0 }}>
              <NavyCircularProgress
                size={52}
                thickness={4}
                variant={showProgress ? "determinate" : "indeterminate"}
                value={progress}
              />
              {message && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#111827",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    {message}
                  </Typography>
                  {renderLoadingDots()}
                  {showProgress && (
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#1e3a8a",
                        fontWeight: 700,
                        mt: 1,
                      }}
                    >
                      {Math.round(progress)}%
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </StyledCard>
        </Grow>
      );

    case "skeleton":
      return (
        <Box sx={{ width: "100%", ...sx }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={120}
            sx={{
              borderRadius: 2,
              mb: 2,
              background: `linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)`,
              backgroundSize: "200px 100%",
              animation: `${shimmer} 1.5s ease-in-out infinite`,
            }}
          />
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          </Box>
          <Skeleton variant="text" width="100%" height={20} />
          <Skeleton variant="text" width="80%" height={20} />
        </Box>
      );

    case "card":
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <StyledCard sx={{ ...sx }}>
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                <NavyCircularProgress size={32} thickness={4} />
                <Typography
                  variant="h6"
                  sx={{
                    color: "#111827",
                    fontWeight: 600,
                  }}
                >
                  {message}
                </Typography>
              </Box>
              {renderLoadingDots()}
            </CardContent>
          </StyledCard>
        </motion.div>
      );

    default:
      return <NavyLinearProgress />;
  }
};

export default LoadingState;
